import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { Infobox } from "@/components/Infobox";
import { GenealogyTree } from "@/components/GenealogyTree";
import { ArticleCard } from "@/components/ArticleCard";
import { FrameCorners, KnotDivider } from "@/components/ornaments";
import { useWikiLinks, LinkPreview, prepareHtml, RichHtml } from "@/components/WikiLinks";
import { PageState } from "@/pages/PageState";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FramedImage } from "@/components/FramedImage";
import { ShieldAlert } from "lucide-react";
import { ratioOf } from "@/lib/aspects";
import { useSpoilers } from "@/context/SpoilerContext";
import { SpoilerSelect } from "@/components/SpoilerShield";
import { useT } from "@/lib/labels";
import { templateOf } from "@/lib/templates";

const CAP_SIZES = { sm: "5rem", md: "7.25rem", lg: "9.5rem", xl: "12rem" };

const attr = (tag, name) => (tag.match(new RegExp(`${name}="([^"]*)"`)) || [])[1] || "";
const spoilerBooks = (html, isUnlocked) => {
  const out = new Map();
  [...(html || "").matchAll(/<span[^>]*data-spoiler[^>]*>/g)].forEach(([t]) => {
    const label = attr(t, "data-book") || "General";
    out.set(label, (out.get(label) ?? true) && isUnlocked(attr(t, "data-book-id"), attr(t, "data-book")));
  });
  return [...out].map(([label, unlocked]) => ({ label, unlocked }));
};

const SpoilerNotice = ({ books, revealed, onToggle }) => {
  const t = useT();
  const hidden = books.filter((b) => !b.unlocked);
  return (
    <div data-testid="spoiler-notice" className="mb-6 px-4 py-3 border border-[#991b1b]/50 bg-[#991b1b]/[0.06] text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <ShieldAlert className="w-4 h-4 text-[#991b1b]" />
        <span data-testid="spoiler-notice-text" className="flex-1">
          {hidden.length
            ? <>Hidden spoilers for <b>{hidden.map((b) => b.label).join(", ")}</b>. Tap a dark passage to reveal it.</>
            : <>Spoilers here are only from books you've finished, so they're shown.</>}
        </span>
        {hidden.length > 0 && t("article.reveal_all") && <button type="button" data-testid="spoiler-toggle-all" onClick={onToggle} className="font-heading text-xs font-bold uppercase tracking-wider border border-[#991b1b] px-3 py-1.5 hover:bg-[#991b1b] hover:text-white transition-colors">{revealed ? t("article.hide_spoilers") : t("article.reveal_all")}</button>}
      </div>
      <div className="mt-3 max-w-sm"><SpoilerSelect testid="article-spoiler-progress" /></div>
    </div>
  );
};

const Body = ({ a, template }) => {
  const hasCap = !!a.drop_cap_image;
  const [revealAll, setRevealAll] = useState(false);
  const { isUnlocked } = useSpoilers();
  const books = useMemo(() => spoilerBooks(a.content_html, isUnlocked), [a.content_html, isUnlocked]);
  const html = useMemo(() => prepareHtml(a.content_html, hasCap && a.drop_cap_replaces_letter, isUnlocked), [a.content_html, hasCap, a.drop_cap_replaces_letter, isUnlocked]);
  const cls = templateOf(template).layout === "poem" ? "poem-verse" : !hasCap ? "illuminate" : "";
  const ratio = ratioOf(a.drop_cap_aspect, "square");
  return (
    <>
    {books.length > 0 && <SpoilerNotice books={books} revealed={revealAll} onToggle={() => setRevealAll((v) => !v)} />}
    <div data-testid="wiki-article-body" className={`codex-prose ${cls} ${revealAll ? "spoilers-revealed" : ""}`}>
      {hasCap && <img data-testid="wiki-article-drop-cap" src={imgUrl(a.drop_cap_image)} alt="" className="drop-cap-img"
        style={{ width: CAP_SIZES[a.drop_cap_size] || CAP_SIZES.md, aspectRatio: ratio || "auto", objectFit: ratio ? "cover" : "contain" }} />}
      <RichHtml className="prose-html" html={html} />
    </div>
    </>
  );
};

const Gallery = ({ items }) => {
  const t = useT();
  const [open, setOpen] = useState(null);
  const list = items.filter((g) => g.url);
  if (!list.length) return null;
  return (
    <section className="mt-16">
      <KnotDivider className="mb-8" />
      {t("article.gallery_eyebrow") && <p className="eyebrow">{t("article.gallery_eyebrow")}</p>}
      {t("article.gallery_title") && <h2 className="font-display text-3xl mt-1 mb-6">{t("article.gallery_title")}</h2>}
      <div data-testid="wiki-gallery" className="columns-2 md:columns-3 lg:columns-4 gap-5">
        {list.map((g, i) => (
          <button key={i} data-testid={`gallery-item-${i}`} onClick={() => setOpen(g)} className="group text-left vellum p-2 lift w-full mb-5 break-inside-avoid block">
            <FramedImage src={g.url} alt={g.caption} aspect={g.aspect} fallback="square" imgClassName="transition-transform duration-700 group-hover:scale-105" />
            {g.caption && <p className="text-sm italic mt-2 px-1 text-[#4a3a2a]">{g.caption}</p>}
          </button>
        ))}
      </div>
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-4xl bg-[#f4efe2] border-2 border-[#c9a227] rounded-none">
          <DialogTitle className="font-heading">{open?.caption || "Illumination"}</DialogTitle>
          {open && <img src={imgUrl(open.url)} alt="" className="w-full max-h-[75vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const LinkList = ({ title, items, testid }) => title && items?.length > 0 && (
  <div data-testid={testid}>
    <p className="eyebrow mb-3">{title}</p>
    <div className="flex flex-wrap gap-2">
      {items.map((i) => i.external
        ? <a key={i.label} href={i.to} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 border border-[#c9a227]/70 bg-[#fbf7ec]/70 font-heading text-sm hover:bg-[#c9a227]/20 transition-colors">{i.label}</a>
        : <Link key={i.label} to={i.to} className="px-3 py-1.5 border border-[#c9a227]/70 bg-[#fbf7ec]/70 font-heading text-sm hover:bg-[#c9a227]/20 transition-colors">{i.label}</Link>)}
    </div>
  </div>
);

export default function ArticlePage() {
  const { slug } = useParams();
  const [a, setA] = useState(undefined);
  useEffect(() => { setA(undefined); window.scrollTo(0, 0); api.get(`/articles/${slug}`).then((r) => setA(r.data)).catch(() => setA(null)); }, [slug]);
  useEffect(() => { if (a?.title) document.title = `${a.title} · Lorekeepers of Elirios`; }, [a]);
  if (!a) return <PageState loading={a === undefined} />;
  return <ArticleView a={a} />;
}

export const ArticleView = ({ a, banner }) => {
  const { handlers, preview } = useWikiLinks();
  const t = useT();
  const template = a.section?.template || "generic";
  const appears = [
    ...(a.books || []).map((b) => ({ to: b.kindle_url || `/wiki/${a.slug}`, label: `Book ${b.number}: ${b.title}`, external: !!b.kindle_url })),
    ...(a.series || []).map((s) => ({ to: `/series/${s.slug}`, label: s.title })),
  ];

  return (
    <>
    {banner}
    <article data-testid="wiki-article-page" {...handlers} className={`max-w-7xl mx-auto px-6 sm:px-8 py-10 theme-${a.theme || "neutral"}`}>
      <nav data-testid="article-breadcrumb" className="text-sm font-heading text-[#5e4a35] mb-6">
        <Link to="/" className="hover:text-[#991b1b]">Home</Link><span className="mx-2">›</span>
        {a.section && <><Link to={`/section/${a.section.slug}`} className="hover:text-[#991b1b]">{a.section.name}</Link><span className="mx-2">›</span></>}
        <span>{a.title}</span>
      </nav>
      {a.hero_image && (
        <figure data-testid="wiki-hero-banner" className="relative vellum gilt p-3 mb-10 rise">
          <FrameCorners />
          <FramedImage src={a.hero_image} alt={a.title} aspect={a.hero_aspect} fallback="panorama" />
          {a.hero_caption && <figcaption className="text-center italic text-sm mt-2 text-[#5e4a35]">{a.hero_caption}</figcaption>}
        </figure>
      )}
      <header className="rise mb-8">
        <p className="eyebrow">{a.section?.name}</p>
        <h1 data-testid="wiki-article-title" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mt-2">{a.title}</h1>
        {a.summary && <p className="mt-4 text-xl italic text-[#4a3a2a] max-w-3xl">{a.summary}</p>}
        {a.author_name && <p data-testid="article-author" className="mt-3 font-heading text-sm text-[#1e4976]">{t("article.contributed")} {a.author_profile
          ? <Link to={`/reader/${a.author_profile}`} data-testid="article-author-link" className="underline decoration-[#c9a227] underline-offset-4 hover:text-[#991b1b]">{a.author_name}</Link>
          : a.author_name}</p>}
      </header>
      <div className="interlace-band mb-10" />
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 lg:order-2"><div className="lg:sticky lg:top-28"><Infobox article={a} template={template} /></div></div>
        <div className="lg:col-span-8 lg:order-1 vellum p-6 sm:p-10 rise" style={{ animationDelay: "120ms" }}><Body a={a} template={template} /></div>
      </div>
      {a.tree?.length > 0 && (
        <section className="mt-16">
          <KnotDivider className="mb-8" />
          {t("article.tree_eyebrow") && <p className="eyebrow">{t("article.tree_eyebrow")}</p>}
          {t("article.tree_title") && <h2 className="font-display text-3xl mt-1 mb-6">{t("article.tree_title")}</h2>}
          <div className="vellum p-4"><GenealogyTree nodes={a.tree} /></div>
        </section>
      )}
      <Gallery items={a.gallery || []} />
      <section className="mt-16 grid md:grid-cols-2 gap-10">
        <LinkList title={t("article.appears")} testid="article-series" items={appears} />
        <LinkList title={t("article.backlinks")} testid="article-backlinks" items={a.backlinks?.map((b) => ({ to: `/wiki/${b.slug}`, label: b.title }))} />
      </section>
      {a.related?.length > 0 && (
        <section className="mt-16">
          <KnotDivider className="mb-8" />
          <p className="eyebrow mb-6">{t("article.more_from")} {a.section?.name}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{a.related.map((r, i) => <ArticleCard key={r.slug} a={r} index={i} />)}</div>
        </section>
      )}
      <LinkPreview preview={preview} />
    </article>
    </>
  );
};
